import { useState, useEffect } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import { signInWithEmailAndPassword, sendEmailVerification } from 'firebase/auth'
import { auth } from '@/lib/firebase'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { AlertCircle } from 'lucide-react'

const friendlyError = (code: string, fallback: string): string => {
  const map: Record<string, string> = {
    'auth/invalid-credential': 'Invalid email or password',
    'auth/user-not-found': 'No account with that email',
    'auth/wrong-password': 'Invalid email or password',
    'auth/invalid-email': 'Please enter a valid email address',
    'auth/too-many-requests': 'Too many attempts. Try again later.',
    'auth/user-disabled': 'This account has been disabled',
  }
  return map[code] ?? fallback
}

export default function Login() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [info, setInfo] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [needsVerification, setNeedsVerification] = useState(false)
  const [searchParams] = useSearchParams()

  useEffect(() => {
    const err = searchParams.get('error')
    const errorDescription = searchParams.get('error_description')
    if (err) setError(errorDescription || 'An error occurred')
  }, [searchParams])

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setInfo(null)
    setNeedsVerification(false)
    setLoading(true)

    try {
      const cred = await signInWithEmailAndPassword(auth, email, password)
      // Re-check freshness of emailVerified in case user just verified
      await cred.user.reload()
      if (!cred.user.emailVerified) {
        setNeedsVerification(true)
        setError('Please verify your email before logging in.')
        return
      }
      // Hard redirect so the Firebase auth state is rehydrated from storage
      // on the /app page load (avoids a race with onAuthStateChanged).
      window.location.href = '/app'
    } catch (err: any) {
      setError(friendlyError(err?.code ?? '', err?.message ?? 'Failed to sign in'))
    } finally {
      setLoading(false)
    }
  }

  const handleResend = async () => {
    if (!auth.currentUser) return
    setError(null)
    try {
      await sendEmailVerification(auth.currentUser)
      setInfo('Verification email sent. Check your inbox.')
    } catch (err: any) {
      setError(err?.message ?? 'Could not resend verification email')
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Admin Login</CardTitle>
          <CardDescription>
            Enter your credentials to access the admin panel
          </CardDescription>
        </CardHeader>
        <form onSubmit={handleLogin}>
          <CardContent className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            {info && (
              <Alert>
                <AlertDescription>{info}</AlertDescription>
              </Alert>
            )}
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="admin@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
              />
            </div>
          </CardContent>
          <CardFooter className="flex flex-col space-y-4">
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? 'Signing in...' : 'Sign in'}
            </Button>
            {needsVerification && (
              <Button type="button" variant="outline" className="w-full" onClick={handleResend}>
                Resend verification email
              </Button>
            )}
            <div className="text-center space-y-2">
              <Link to="/" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                ← Back to Home
              </Link>
              <div className="text-xs text-muted-foreground">
                Don't have an account? <Link to="/register" className="text-primary hover:underline">Sign up here</Link>
              </div>
              <div className="text-xs text-muted-foreground">
                <Link to="/reset-password" className="text-primary hover:underline">Forgot password?</Link>
              </div>
            </div>
          </CardFooter>
        </form>
      </Card>
    </div>
  )
}
